import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Returns true when `path` is "within" the given basePath — i.e. exact match or
 * a sub-route. Avoids the `startsWith` footguns where basePath `''` or `/`
 * would match every path, or basePath `/foo` would match `/foobar`.
 */
const LEGACY_WORKFORCE_PLANNING_PREFIX = '/workforce-planning';
const PERSONNEL_BUDGETING_PREFIX = '/personnel-budgeting';

function isPathWithinBase(path: string, basePath: string): boolean {
  if (basePath === '') return false;
  if (path === basePath) return true;
  // The root basePath only matches itself; treating it as a prefix would match
  // every navigation in the app (since every path starts with '/').
  if (basePath === '/') return false;
  return path.startsWith(basePath + '/');
}

/**
 * A stored link can move from the new mount back to the old one for one
 * navigation. That hop stays inside Personnel Budgeting, so it must not
 * count as leaving the app.
 */
export function aliasLegacyWorkforcePlanningPath(path: string): string {
  const lower = path.toLowerCase();
  if (lower === LEGACY_WORKFORCE_PLANNING_PREFIX) return PERSONNEL_BUDGETING_PREFIX;
  if (lower.startsWith(`${LEGACY_WORKFORCE_PLANNING_PREFIX}/`)) {
    return `${PERSONNEL_BUDGETING_PREFIX}${path.slice(LEGACY_WORKFORCE_PLANNING_PREFIX.length)}`;
  }
  return path;
}

/**
 * Detects navigation away from apps/routes that require page refresh and triggers full page reload.
 * Prevents "Dirty DOM" issues (DOM artifacts, event listeners, CSS leaks, WebGL context issues) by giving the next app a clean browser environment.
 *
 * **How it works:**
 * - Receives an array of basePaths that have `triggerPageRefreshOnNavigateAway: true`
 * - React Router's useLocation() detects ALL navigation changes (sidebar clicks, direct URLs, programmatic navigate, back button, History API, etc.)
 * - When navigating AWAY from a path in the refreshBasePaths array, triggers window.location.href to refresh the page
 *
 * **Usage:**
 * ```typescript
 * // In parent component, pre-filter the basePaths that require refresh
 * const refreshBasePaths = [
 *   ...pspContext.appRegistry
 *     ?.filter(app => app.triggerPageRefreshOnNavigateAway)
 *     .map(app => app.basePath),
 *   ...pspContext.routeRegistry
 *     ?.filter(route => route.triggerPageRefreshOnNavigateAway)
 *     .map(route => route.basePath),
 * ].filter(Boolean);
 *
 * usePageRefreshOnNavigation(refreshBasePaths);
 * ```
 *
 * **Note:** The `refreshBasePaths` parameter is required. This allows the hook to:
 * - Be framework-agnostic (only depends on router location and an array)
 * - Be trivially easy to test (pass a simple string array)
 * - Avoid any registry lookup logic
 */
export function usePageRefreshOnNavigation(refreshBasePaths: readonly string[]) {
  const location = useLocation();
  const previousPathRef = useRef<string>(location.pathname);

  // Mirror refreshBasePaths into a ref so the navigation effect can read the
  // latest value without depending on it. Without this, registry updates that
  // change refreshBasePaths would re-fire the effect and compare a stale
  // previousPathRef against the current pathname — producing phantom "leaving
  // /foo" events long after the actual location change.
  const refreshBasePathsRef = useRef<readonly string[]>([]);
  refreshBasePathsRef.current = refreshBasePaths;

  useEffect(() => {
    const currentPath = aliasLegacyWorkforcePlanningPath(location.pathname);
    const previousPath = aliasLegacyWorkforcePlanningPath(previousPathRef.current);

    // Always advance the ref so the next location change compares against
    // where we are now, not where the component first mounted.
    previousPathRef.current = location.pathname;

    if (currentPath === previousPath) {
      return;
    }

    // Refresh only when truly leaving an app — i.e. previousPath was inside a
    // refresh-enabled basePath and currentPath is NOT inside that same base.
    // Without this guard, sub-route navigation within an app (e.g. opening a
    // drawer that adds an `/:id` segment) would trigger a full page reload.
    const needsRefresh = refreshBasePathsRef.current.some(
      (basePath) =>
        isPathWithinBase(previousPath, basePath) && !isPathWithinBase(currentPath, basePath)
    );

    console.log(`[LSF] Navigation: ${previousPath} → ${currentPath}`);

    if (needsRefresh) {
      console.log(`[LSF] ✓ Refresh enabled on "${previousPath}": triggering page reload`);
      window.location.href = `${location.pathname}${location.search}${location.hash}`;
    } else {
      console.log('[LSF] ✗ No refresh needed: React Router handles navigation');
    }
  }, [location.pathname]);
}
